/**
 * Token Refresh Interceptor Utility
 *
 * Provides a proactive token-refresh mechanism that fires on every outbound
 * API call.  When the current access token has less than TOKEN_REFRESH_THRESHOLD
 * minutes remaining, the interceptor silently exchanges the stored refresh token
 * for a new access token before the request is sent.
 *
 * Usage (in any service that creates an axios instance):
 *
 *   import { applyTokenRefreshInterceptor } from '../utils/tokenRefreshInterceptor';
 *
 *   const myApiClient = axios.create({ ... });
 *   // ... existing interceptors ...
 *   applyTokenRefreshInterceptor(myApiClient);
 *
 * Because axios executes request interceptors in LIFO order the refresh check
 * will run *before* the existing auth-header interceptor, so the fresh token is
 * always written to localStorage before the auth header is read.
 */

import config from '../config';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Refresh when fewer than this many minutes remain on the access token. */
const TOKEN_REFRESH_THRESHOLD_MINUTES = 30;

/** localStorage keys */
const TOKEN_KEY = 'token';
const COGNITO_ACCESS_TOKEN_KEY = 'cognito_access_token';
const COGNITO_REFRESH_TOKEN_KEY = 'cognito_refresh_token';
const COGNITO_USERNAME_KEY = 'cognito_username';
const COGNITO_EXPIRES_AT_KEY = 'cognito_expires_at';

// ─── Shared refresh-in-progress promise ──────────────────────────────────────
// Prevents multiple concurrent refresh calls when several requests fire at the
// same time while the token is near expiry.
let _refreshPromise = null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Decode the `exp` claim from a JWT without a library.
 *
 * @param {string} token
 * @returns {number|null} Expiration timestamp in seconds, or null if unreadable.
 */
const getTokenExpiration = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
};

/**
 * Return true when the token will expire within the threshold window.
 *
 * @param {string} token
 * @returns {boolean}
 */
const isTokenNearExpiration = (token) => {
  const exp = getTokenExpiration(token);
  if (!exp) return true; // Cannot read expiry – treat as near-expired

  const now = Math.floor(Date.now() / 1000);
  const ttlSeconds = exp - now;
  return ttlSeconds <= TOKEN_REFRESH_THRESHOLD_MINUTES * 60;
};

/**
 * Call the auth service's /auth/refresh endpoint and update localStorage.
 * Resolves once the refresh is complete, whether it succeeded or not (errors
 * are swallowed so they do not block the original request).
 */
const performRefresh = async () => {
  const refreshToken = localStorage.getItem(COGNITO_REFRESH_TOKEN_KEY);
  const username = localStorage.getItem(COGNITO_USERNAME_KEY);

  if (!refreshToken) {
    // No refresh token stored – nothing we can do proactively.
    return;
  }

  try {
    const response = await fetch(
      `${config.api.authServiceUrl}/auth/refresh`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken, username }),
      }
    );

    if (!response.ok) {
      console.warn('[tokenRefreshInterceptor] Refresh request failed:', response.status);
      return;
    }

    const data = await response.json();

    if (data.access_token) {
      // Update both the generic token key and the Cognito-specific key so that
      // all parts of the application see the refreshed credentials.
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, data.access_token);

      if (data.expires_in) {
        const expiresAt = Date.now() + data.expires_in * 1000;
        localStorage.setItem(COGNITO_EXPIRES_AT_KEY, expiresAt.toString());
      }

      // Persist any new refresh token if the server rotated it.
      if (data.refresh_token) {
        localStorage.setItem(COGNITO_REFRESH_TOKEN_KEY, data.refresh_token);
      }

      console.debug('[tokenRefreshInterceptor] Token refreshed proactively.');
    }
  } catch (err) {
    // Network error, etc. – let the request proceed with the existing token.
    console.warn('[tokenRefreshInterceptor] Refresh error (non-fatal):', err);
  }
};

/**
 * Check the current token and, if it is within the refresh window, perform a
 * token refresh before the request is sent.  Concurrent calls share a single
 * in-flight refresh promise to avoid hammering the auth endpoint.
 */
const refreshTokenIfNeeded = async () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  if (!isTokenNearExpiration(token)) return;

  // Share an in-flight promise so concurrent requests only refresh once.
  if (!_refreshPromise) {
    _refreshPromise = performRefresh().finally(() => {
      _refreshPromise = null;
    });
  }

  await _refreshPromise;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Attach a proactive token-refresh request interceptor to the given axios
 * instance.
 *
 * Call this *after* adding any existing interceptors so that it is inserted at
 * the front of the chain and runs first for every request.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export const applyTokenRefreshInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (cfg) => {
      await refreshTokenIfNeeded();
      return cfg;
    },
    (error) => Promise.reject(error)
  );
};

export default applyTokenRefreshInterceptor;
